import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import type { IdentityProvider, CreateIdentityProviderRequest } from '@/types/identity-provider';

export async function getIdentityProviders(): Promise<IdentityProvider[]> {
  const res = await apiGet<{ data: IdentityProvider[] }>('/identity-providers');
  return res.data;
}

export function getIdentityProvider(id: string): Promise<IdentityProvider> {
  return apiGet<IdentityProvider>(`/identity-providers/${id}`);
}

export function createIdentityProvider(data: CreateIdentityProviderRequest): Promise<IdentityProvider> {
  return apiPost<IdentityProvider>('/identity-providers', data);
}

export function syncIdentityProvider(id: string): Promise<{ message: string }> {
  return apiPost<{ message: string }>(`/identity-providers/${id}/sync`);
}

export function updateIdentityProvider(id: string, data: Partial<CreateIdentityProviderRequest>): Promise<IdentityProvider> {
  return apiPatch<IdentityProvider>(`/identity-providers/${id}`, data);
}

export function deleteIdentityProvider(id: string): Promise<void> {
  return apiDelete(`/identity-providers/${id}`);
}

// Policy Suggestions

export interface PolicySuggestion {
  suggestion_id: string;
  type: string;
  template_id: string;
  name: string;
  description: string;
  confidence: number;
  reasoning: string;
  placeholder_values: Record<string, string>;
  sample_principals: string[];
  member_count: number;
  category: string;
}

export interface PolicySuggestionsResponse {
  suggestions: PolicySuggestion[];
  principal_count: number;
  analysis_summary: string;
}

export interface ApplyPolicySuggestionsResponse {
  applied: number;
  policies: { suggestion_id: string; policy_id: string; policy_name: string; status: string }[];
  errors: { suggestion_id: string; error: string }[];
  status: string;
}

export function suggestPolicies(idpId: string): Promise<PolicySuggestionsResponse> {
  return apiPost<PolicySuggestionsResponse>(`/identity-providers/${idpId}/suggest-policies`);
}

export function applyPolicySuggestions(
  idpId: string,
  suggestionIds: string[],
  suggestionsData: PolicySuggestion[]
): Promise<ApplyPolicySuggestionsResponse> {
  return apiPost<ApplyPolicySuggestionsResponse>(`/identity-providers/${idpId}/apply-policy-suggestions`, {
    suggestion_ids: suggestionIds,
    suggestions_data: suggestionsData,
  });
}

// SCIM Token Management

export interface ScimTokenResponse {
  token: string;
  id: string;
  identity_provider_id: string;
  created_at: string;
  expires_at: string | null;
}

export function generateScimToken(idpId: string): Promise<ScimTokenResponse> {
  return apiPost<ScimTokenResponse>(`/identity-providers/${idpId}/scim-token`);
}

export function revokeScimToken(idpId: string): Promise<void> {
  return apiDelete(`/identity-providers/${idpId}/scim-token`);
}
